/**
 * Mock Data for Agent Marketplace
 * UF-008: Agent Marketplace UI
 */

import type { MarketplaceAgent } from "./types";

// =============================================================================
// Mock Agents
// =============================================================================

export const MOCK_AGENTS: MarketplaceAgent[] = [
  {
    id: "agent-001",
    name: "Neural Classifier Pro",
    shortDescription: "Advanced ML classification with FHE-encrypted inference",
    fullDescription: `Neural Classifier Pro is a state-of-the-art machine learning agent designed for high-accuracy classification tasks while maintaining complete data privacy through FHE encryption.

Key Features:
- Multi-class classification with 99.2% accuracy on standard benchmarks
- Real-time inference with sub-100ms latency
- Automatic feature engineering and selection
- Built-in model explainability with SHAP values
- Seamless integration with HVME mesh orchestration

Perfect for healthcare, finance, and any domain requiring both high accuracy and complete data privacy.`,
    icon: "brain",
    iconColor: "#758eff",
    category: "ml",
    capabilities: [
      { id: "cap-1", name: "Binary Classification", description: "Two-class prediction with confidence scores" },
      { id: "cap-2", name: "Multi-class Classification", description: "Support for up to 1000 classes" },
      { id: "cap-3", name: "FHE Inference", description: "Fully homomorphic encrypted model execution" },
      { id: "cap-4", name: "Auto Feature Selection", description: "Automatic identification of relevant features" },
    ],
    pricing: { model: "subscription", price: 49, currency: "USD", billingPeriod: "monthly" },
    rating: 4.8,
    reviewCount: 234,
    downloads: 15420,
    publisher: "HVME Labs",
    publisherVerified: true,
    version: "2.3.1",
    lastUpdated: "2025-01-28",
    createdAt: "2024-06-15",
    tags: ["classification", "ml", "fhe", "privacy", "neural-network"],
    reviews: [
      {
        id: "rev-1",
        userId: "user-001",
        userName: "DataScientist42",
        rating: 5,
        comment: "Incredible accuracy and the FHE integration is seamless. Our compliance team is thrilled!",
        createdAt: "2025-01-25",
        helpful: 45,
      },
      {
        id: "rev-2",
        userId: "user-002",
        userName: "MLEngineer_Pro",
        rating: 4,
        comment: "Great performance, though initial setup took some time. Documentation could be better.",
        createdAt: "2025-01-20",
        helpful: 23,
      },
    ],
    owned: false,
    deployed: false,
  },
  {
    id: "agent-002",
    name: "DataFlow Analyzer",
    shortDescription: "Real-time analytics with privacy-preserving aggregations",
    fullDescription: `DataFlow Analyzer provides powerful real-time analytics capabilities while ensuring your data never leaves your secure environment unencrypted.

Key Features:
- Stream processing at 100K events/second
- Privacy-preserving aggregations using secure MPC
- Custom metric definitions with SQL-like syntax
- Automatic anomaly detection
- Beautiful visualization dashboards

Ideal for real-time monitoring, business intelligence, and operational analytics.`,
    icon: "chart-line",
    iconColor: "#d61ef4",
    category: "analytics",
    capabilities: [
      { id: "cap-1", name: "Stream Processing", description: "High-throughput real-time data processing" },
      { id: "cap-2", name: "Anomaly Detection", description: "ML-powered outlier identification" },
      { id: "cap-3", name: "Custom Metrics", description: "Define custom KPIs with simple expressions" },
      { id: "cap-4", name: "Secure Aggregation", description: "Privacy-preserving data summaries" },
    ],
    pricing: { model: "paid", price: 299, currency: "USD", billingPeriod: "one-time" },
    rating: 4.6,
    reviewCount: 156,
    downloads: 8932,
    publisher: "StreamTech Inc",
    publisherVerified: true,
    version: "1.8.0",
    lastUpdated: "2025-01-22",
    createdAt: "2024-09-01",
    tags: ["analytics", "streaming", "real-time", "visualization"],
    reviews: [
      {
        id: "rev-3",
        userId: "user-003",
        userName: "AnalyticsGuru",
        rating: 5,
        comment: "Best analytics tool we've ever used. The privacy features are a huge plus.",
        createdAt: "2025-01-18",
        helpful: 38,
      },
    ],
    owned: true,
    deployed: true,
  },
  {
    id: "agent-003",
    name: "ETL Pipeline Builder",
    shortDescription: "Visual ETL with encrypted data transformations",
    fullDescription: `ETL Pipeline Builder makes data engineering easy with a visual drag-and-drop interface for building complex data pipelines.

Key Features:
- Visual pipeline designer with 100+ connectors
- Encrypted-at-rest and in-transit data handling
- Automatic schema detection and mapping
- Built-in data quality checks
- Scheduling and orchestration

Perfect for data teams who need robust, secure data pipelines without coding.`,
    icon: "git-branch",
    iconColor: "#4aff7a",
    category: "data-processing",
    capabilities: [
      { id: "cap-1", name: "Visual Designer", description: "Drag-and-drop pipeline creation" },
      { id: "cap-2", name: "100+ Connectors", description: "Pre-built integrations for popular data sources" },
      { id: "cap-3", name: "Data Validation", description: "Automatic quality checks and alerts" },
      { id: "cap-4", name: "Scheduling", description: "Cron-based and event-driven execution" },
    ],
    pricing: { model: "free" },
    rating: 4.4,
    reviewCount: 89,
    downloads: 23456,
    publisher: "OpenPipe Community",
    publisherVerified: false,
    version: "3.0.2",
    lastUpdated: "2025-01-15",
    createdAt: "2024-03-20",
    tags: ["etl", "pipeline", "data-engineering", "visual"],
    reviews: [],
    owned: true,
    deployed: false,
  },
  {
    id: "agent-004",
    name: "Sentiment Analyzer",
    shortDescription: "NLP-powered sentiment analysis with multilingual support",
    fullDescription: `Sentiment Analyzer uses cutting-edge NLP models to analyze text sentiment across 50+ languages.

Key Features:
- Support for 50+ languages
- Aspect-based sentiment analysis
- Emotion detection (joy, anger, sadness, etc.)
- Social media optimization
- Batch and real-time processing

Great for brand monitoring, customer feedback analysis, and social listening.`,
    icon: "message-circle",
    iconColor: "#758eff",
    category: "ml",
    capabilities: [
      { id: "cap-1", name: "Sentiment Detection", description: "Positive, negative, neutral classification" },
      { id: "cap-2", name: "Emotion Analysis", description: "Fine-grained emotion detection" },
      { id: "cap-3", name: "Aspect Extraction", description: "Identify topics within text" },
      { id: "cap-4", name: "Multilingual", description: "50+ language support" },
    ],
    pricing: { model: "subscription", price: 29, currency: "USD", billingPeriod: "monthly" },
    rating: 4.7,
    reviewCount: 312,
    downloads: 18234,
    publisher: "NLP Solutions",
    publisherVerified: true,
    version: "4.1.0",
    lastUpdated: "2025-01-26",
    createdAt: "2024-01-10",
    tags: ["nlp", "sentiment", "text-analysis", "multilingual"],
    reviews: [],
    owned: false,
    deployed: false,
  },
  {
    id: "agent-005",
    name: "Fraud Detection Suite",
    shortDescription: "Real-time fraud detection with explainable AI",
    fullDescription: `Fraud Detection Suite combines multiple ML models to identify fraudulent transactions in real-time with full explainability.

Key Features:
- Ensemble of 12 specialized fraud models
- Sub-10ms inference latency
- Full explainability for compliance
- Adaptive learning from feedback
- Custom rule integration

Designed for fintech, e-commerce, and any transaction-heavy business.`,
    icon: "shield",
    iconColor: "#d61ef4",
    category: "analytics",
    capabilities: [
      { id: "cap-1", name: "Real-time Scoring", description: "Transaction risk scores in milliseconds" },
      { id: "cap-2", name: "Explainability", description: "Human-readable reasons for decisions" },
      { id: "cap-3", name: "Adaptive Learning", description: "Continuously improves from feedback" },
      { id: "cap-4", name: "Rule Engine", description: "Combine ML with custom business rules" },
    ],
    pricing: { model: "subscription", price: 199, currency: "USD", billingPeriod: "monthly" },
    rating: 4.9,
    reviewCount: 78,
    downloads: 4521,
    publisher: "SecureAI Corp",
    publisherVerified: true,
    version: "1.5.3",
    lastUpdated: "2025-01-27",
    createdAt: "2024-11-01",
    tags: ["fraud", "security", "fintech", "explainable-ai"],
    reviews: [],
    owned: false,
    deployed: false,
  },
  {
    id: "agent-006",
    name: "Data Anonymizer",
    shortDescription: "GDPR-compliant data anonymization and masking",
    fullDescription: `Data Anonymizer helps you comply with privacy regulations by automatically detecting and anonymizing PII.

Key Features:
- Automatic PII detection across 30+ data types
- Multiple anonymization techniques
- GDPR, HIPAA, CCPA compliant
- Reversible tokenization option
- Audit logging for compliance

Essential for any organization handling personal data.`,
    icon: "eye-off",
    iconColor: "#4aff7a",
    category: "data-processing",
    capabilities: [
      { id: "cap-1", name: "PII Detection", description: "Automatically find sensitive data" },
      { id: "cap-2", name: "Masking", description: "Replace sensitive values with masked versions" },
      { id: "cap-3", name: "Tokenization", description: "Reversible data protection" },
      { id: "cap-4", name: "Compliance Reports", description: "Generate audit-ready documentation" },
    ],
    pricing: { model: "paid", price: 149, currency: "USD", billingPeriod: "one-time" },
    rating: 4.5,
    reviewCount: 167,
    downloads: 12890,
    publisher: "PrivacyFirst Labs",
    publisherVerified: true,
    version: "2.2.0",
    lastUpdated: "2025-01-20",
    createdAt: "2024-04-15",
    tags: ["privacy", "gdpr", "anonymization", "compliance"],
    reviews: [],
    owned: false,
    deployed: false,
  },
  {
    id: "agent-007",
    name: "Custom Agent Scaffold",
    shortDescription: "Template for building your own HVME agents",
    fullDescription: `Custom Agent Scaffold provides everything you need to build and deploy your own agents on the HVME platform.

Key Features:
- Pre-configured project structure
- FHE integration templates
- Testing and debugging tools
- Deployment automation
- Comprehensive documentation

Perfect for developers who want to create custom agents.`,
    icon: "code",
    iconColor: "#E6BEFA",
    category: "custom",
    capabilities: [
      { id: "cap-1", name: "Project Template", description: "Pre-configured agent structure" },
      { id: "cap-2", name: "FHE SDK", description: "Easy encryption integration" },
      { id: "cap-3", name: "Debug Tools", description: "Local testing environment" },
      { id: "cap-4", name: "CI/CD", description: "Automated deployment pipelines" },
    ],
    pricing: { model: "free" },
    rating: 4.3,
    reviewCount: 45,
    downloads: 5678,
    publisher: "HVME Labs",
    publisherVerified: true,
    version: "1.0.0",
    lastUpdated: "2025-01-10",
    createdAt: "2025-01-01",
    tags: ["development", "template", "custom", "sdk"],
    reviews: [],
    owned: false,
    deployed: false,
  },
  {
    id: "agent-008",
    name: "Predictive Maintenance",
    shortDescription: "IoT sensor data analysis for equipment health",
    fullDescription: `Predictive Maintenance uses advanced time-series analysis to predict equipment failures before they happen.

Key Features:
- Support for 100+ sensor types
- Anomaly detection and trending
- Remaining useful life prediction
- Integration with SCADA systems
- Mobile alerts and dashboards

Essential for manufacturing, energy, and any asset-heavy industry.`,
    icon: "settings",
    iconColor: "#758eff",
    category: "ml",
    capabilities: [
      { id: "cap-1", name: "Sensor Integration", description: "Connect to industrial IoT devices" },
      { id: "cap-2", name: "Failure Prediction", description: "ML-based equipment health scoring" },
      { id: "cap-3", name: "RUL Estimation", description: "Remaining useful life calculation" },
      { id: "cap-4", name: "Alert System", description: "Configurable notifications" },
    ],
    pricing: { model: "subscription", price: 399, currency: "USD", billingPeriod: "monthly" },
    rating: 4.6,
    reviewCount: 92,
    downloads: 3421,
    publisher: "Industrial AI Inc",
    publisherVerified: true,
    version: "2.0.1",
    lastUpdated: "2025-01-24",
    createdAt: "2024-07-01",
    tags: ["iot", "predictive", "maintenance", "industrial"],
    reviews: [],
    owned: false,
    deployed: false,
  },
];
